import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PlanCard } from "../../components/plan-card.js";
import {
  useCreateCheckoutSession,
  useStartTrial,
} from "../../hooks/use-billing.js";
import { useAdvanceWizardStep } from "../../hooks/use-wizard.js";
import { apiPost } from "../../api/client.js";
import type { WizardState } from "../../api/types.js";

interface PlanSelectionStepProps {
  websiteId: string;
  wizardState: WizardState;
  orgId: string;
}

const PLAN_FEATURES = {
  free: [
    { label: "1 website", included: true },
    { label: "10 pages per scan", included: true },
    { label: "1 scan / month", included: true },
    { label: "Document scanning", included: false },
    { label: "Regression detection", included: false },
  ],
  starter: [
    { label: "1 website", included: true },
    { label: "150 pages per scan", included: true },
    { label: "2 scans / month", included: true },
    { label: "Document scanning", included: false },
    { label: "Regression detection", included: false },
  ],
  growth: [
    { label: "3 websites", included: true },
    { label: "250 pages per scan", included: true },
    { label: "4 scans / month", included: true },
    { label: "Document scanning", included: true },
    { label: "Regression detection", included: false },
  ],
  pro: [
    { label: "5 websites", included: true },
    { label: "1,000 pages per scan", included: true },
    { label: "8 scans / month", included: true },
    { label: "Document scanning", included: true },
    { label: "Regression detection", included: true },
  ],
};

export function PlanSelectionStep({
  websiteId,
  orgId,
}: PlanSelectionStepProps) {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showContactForm, setShowContactForm] = useState(false);
  const navigate = useNavigate();

  const checkout = useCreateCheckoutSession();
  const trial = useStartTrial();
  const advance = useAdvanceWizardStep(websiteId);

  async function handleSelectPlan(planId: string) {
    setSelectedPlan(planId);
    setErrorMessage("");
    try {
      const result = await checkout.mutateAsync({ orgId, planId, websiteId });
      if (!result.checkoutUrl?.startsWith("https://")) {
        throw new Error("Invalid checkout URL");
      }
      window.location.href = result.checkoutUrl;
    } catch (err) {
      setSelectedPlan(null);
      const message = err instanceof Error ? err.message : String(err);
      setErrorMessage(message || "Failed to create checkout session. Please try again.");
      console.error("Checkout error:", err);
    }
  }

  async function handleStartFree() {
    setSelectedPlan("free");
    setErrorMessage("");
    try {
      await trial.mutateAsync({ orgId, planId: "free", websiteId });
      navigate(`/wizard/${websiteId}`);
    } catch (err) {
      setSelectedPlan(null);
      const message = err instanceof Error ? err.message : String(err);
      setErrorMessage(message || "Failed to activate free plan. Please try again.");
      console.error("Free plan error:", err);
    }
  }

  const isLoading = checkout.isPending || trial.isPending;
  const displayError = errorMessage || (checkout.error ? checkout.error.message : null) || (trial.error ? trial.error.message : null);

  return (
    <div className="wizard-content">
      <h2 className="section-title">Choose your plan</h2>
      <p className="text-muted" style={{ marginBottom: "24px" }}>
        Start free with 10 pages, or choose a plan for deeper scanning.
        Paid plans include a 14-day free trial.
      </p>

      {(checkout.isError || trial.isError || displayError) && (
        <div className="alert alert--error" style={{ marginBottom: "16px" }}>
          {displayError || "Something went wrong. Please try again."}
        </div>
      )}

      <div className="plan-grid">
        <PlanCard
          id="free"
          name="Free"
          price="€0"
          features={PLAN_FEATURES.free}
          ctaLabel="Start free"
          onSelect={() => void handleStartFree()}
          isLoading={isLoading && selectedPlan === "free"}
        />
        <PlanCard
          id="starter"
          name="Starter"
          price="€10 / month"
          features={PLAN_FEATURES.starter}
          ctaLabel="Start with Starter"
          onSelect={() => void handleSelectPlan("starter")}
          isLoading={isLoading && selectedPlan === "starter"}
        />
        <PlanCard
          id="growth"
          name="Growth"
          price="€30 / month"
          features={PLAN_FEATURES.growth}
          isPopular
          ctaLabel="Start with Growth"
          onSelect={() => void handleSelectPlan("growth")}
          isLoading={isLoading && selectedPlan === "growth"}
        />
        <PlanCard
          id="pro"
          name="Pro"
          price="€99 / month"
          features={PLAN_FEATURES.pro}
          ctaLabel="Start with Pro"
          onSelect={() => void handleSelectPlan("pro")}
          isLoading={isLoading && selectedPlan === "pro"}
        />
        <PlanCard
          id="enterprise"
          name="Enterprise"
          price="Custom"
          features={[
            { label: "Unlimited websites", included: true },
            { label: "Unlimited pages", included: true },
            { label: "All features", included: true },
            { label: "Priority support & SLA", included: true },
            { label: "Custom integrations", included: true },
          ]}
          isEnterprise
          ctaLabel="Get in touch"
          onSelect={() => setShowContactForm(true)}
        />
      </div>

      {showContactForm && (
        <EnterpriseContactForm
          onClose={() => setShowContactForm(false)}
          orgId={orgId}
          advance={advance}
        />
      )}
    </div>
  );
}

/* ── Enterprise Contact Form (GDPR-compliant) ────────────────────────────── */

interface ContactFormState {
  name: string;
  email: string;
  company: string;
  websites: string;
  message: string;
  gdprConsent: boolean;
}

function EnterpriseContactForm({
  onClose,
  orgId,
  advance,
}: {
  onClose: () => void;
  orgId: string;
  advance: ReturnType<typeof useAdvanceWizardStep>;
}) {
  const [form, setForm] = useState<ContactFormState>({
    name: "",
    email: "",
    company: "",
    websites: "",
    message: "",
    gdprConsent: false,
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  function handleChange(
    e: FormEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const target = e.currentTarget;
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      setForm((prev) => ({ ...prev, [target.id]: target.checked }));
    } else {
      setForm((prev) => ({ ...prev, [target.id]: target.value }));
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.gdprConsent) {
      setErrorMsg("Please confirm you agree to the data processing terms.");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");

    try {
      await apiPost("/public/contact", {
        name: form.name,
        email: form.email,
        company: form.company,
        topic: "sales",
        message: `[Enterprise enquiry — Org: ${orgId}]\nEstimated websites: ${form.websites || "Not specified"}\n\n${form.message}`,
      });

      setStatus("success");
      advance.mutate({
        step: "plan_selection",
        outcome: "advisory",
        data: { planId: "enterprise", enterpriseInquirySent: true },
      });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
    }
  }

  if (status === "success") {
    return (
      <div
        className="product-section"
        style={{ marginTop: "24px", padding: "24px", background: "#f0fdf4", borderRadius: "8px" }}
      >
        <h3 className="section-title">Thanks for your interest</h3>
        <p className="text-muted">
          We've received your enquiry and will be in touch within 24 hours
          to discuss your enterprise requirements.
        </p>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          style={{ marginTop: "12px" }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div
      className="product-section"
      style={{ marginTop: "24px", padding: "24px", border: "1px solid var(--color-border)", borderRadius: "8px" }}
    >
      <h3 className="section-title">Enterprise enquiry</h3>
      <p className="text-muted text-sm" style={{ marginBottom: "16px" }}>
        Tell us about your needs and we'll prepare a custom proposal.
      </p>

      {status === "error" && (
        <div className="alert alert--error" style={{ marginBottom: "12px" }}>
          {errorMsg || "Failed to send. Please try again."}
        </div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)}>
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            id="name"
            type="text"
            required
            value={form.name}
            onChange={handleChange}
            disabled={status === "submitting"}
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Work email *</label>
          <input
            id="email"
            type="email"
            required
            value={form.email}
            onChange={handleChange}
            disabled={status === "submitting"}
          />
        </div>

        <div className="form-group">
          <label htmlFor="company">Company *</label>
          <input
            id="company"
            type="text"
            required
            value={form.company}
            onChange={handleChange}
            disabled={status === "submitting"}
          />
        </div>

        <div className="form-group">
          <label htmlFor="websites">Estimated number of websites</label>
          <select
            id="websites"
            value={form.websites}
            onChange={handleChange}
            disabled={status === "submitting"}
          >
            <option value="">Select</option>
            <option value="5-10">5–10</option>
            <option value="10-25">10–25</option>
            <option value="25-50">25–50</option>
            <option value="50+">50+</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="message">Tell us about your requirements *</label>
          <textarea
            id="message"
            rows={4}
            required
            value={form.message}
            onChange={handleChange}
            disabled={status === "submitting"}
            placeholder="Number of pages, compliance requirements, integrations needed, etc."
          ></textarea>
        </div>

        <div className="form-group" style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <input
            id="gdprConsent"
            type="checkbox"
            checked={form.gdprConsent}
            onChange={handleChange}
            disabled={status === "submitting"}
            style={{ marginTop: "4px" }}
          />
          <label htmlFor="gdprConsent" className="text-sm">
            I agree that AccessiMonitor may process my personal data (name, email,
            company) to respond to this enquiry. Data is processed under GDPR
            Article 6(1)(b) (pre-contractual measures) and retained for no longer
            than 12 months. You can request deletion at any time by emailing{" "}
            <a href="mailto:privacy@accessimonitor.app">privacy@accessimonitor.app</a>.
          </label>
        </div>

        <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? "Sending..." : "Send enquiry"}
          </button>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
            disabled={status === "submitting"}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
